import { type IntegrationEndpoint } from '@/store/slices/integrationSlice';
import { envConfig } from './env-config';

/**
 * Cloud Function URLs are constructed using the Google Project ID from the environment.
 * Each function has its own unique URL.
 */
const GOOGLE_PROJECT_ID = import.meta.env.VITE_GOOGLE_PROJECT_ID;

// All cloud functions have been migrated to the gateway API (see gateway-service.ts).
// This object is kept for type compatibility with existing code that references it.
export const cloudFunctionUrls = {} as Record<string, string>;

/**
 * HTTP Methods supported by cloud functions
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Request configuration for cloud function calls
 */
export interface CloudFunctionRequestConfig {
  method?: HttpMethod;
  headers?: Record<string, string>;
  timeout?: number;
  retryAttempts?: number;
  useQueryParams?: boolean; // Whether to send params as query params instead of body
  cacheKey?: string; // For request deduplication
  skipLogging?: boolean; // Skip request/response logging
}

/**
 * Cloud Function Service Configuration
 */
export interface CloudFunctionServiceConfig {
  defaultTimeout?: number;
  defaultRetryAttempts?: number;
  defaultHeaders?: Record<string, string>;
  enableRequestDeduplication?: boolean;
  enableDetailedLogging?: boolean;
  maxCallHistorySize?: number;
}

/**
 * Cloud Function Call Parameters
 */
export interface CloudFunctionCallParams {
  secretId?: string;
  username?: string;
  url?: string;
  [key: string]: unknown; // Allow additional parameters
}

/**
 * Cloud Function Call Result
 */
export interface CloudFunctionCallResult {
  success: boolean;
  data?: unknown;
  error?: string;
  statusCode?: number;
  responseTime?: number;
  requestId?: string; // For debugging and tracing
  retryCount?: number; // Number of retries attempted
}

/**
 * Cloud Function Definition with enhanced configuration
 */
export interface CloudFunctionDefinition {
  name: keyof typeof cloudFunctionUrls;
  description: string;
  requiredParams: string[];
  optionalParams?: string[];
  defaultTimeout?: number;
  defaultMethod?: HttpMethod;
  useQueryParams?: boolean; // Default for this function
  retryConfig?: {
    maxRetries: number;
    backoffMultiplier: number;
    initialDelay: number;
    retryableStatusCodes?: number[]; // Status codes that should trigger retry
  };
  headers?: Record<string, string>; // Default headers for this function
}

/**
 * Call History Entry with enhanced information
 */
export interface CallHistoryEntry {
  timestamp: number;
  functionName: string;
  success: boolean;
  responseTime: number;
  error?: string;
  statusCode?: number;
  requestId: string;
  method: HttpMethod;
  params?: Record<string, unknown>;
}

/**
 * Request Interceptor for adding authentication, logging, etc.
 */
export interface RequestInterceptor {
  name: string;
  intercept: (request: Request) => Promise<Request> | Request;
}

/**
 * Response Interceptor for processing responses
 */
export interface ResponseInterceptor {
  name: string;
  intercept: (
    response: Response,
    requestId: string,
  ) => Promise<Response> | Response;
}

/**
 * Enhanced Google Cloud Function Service
 * Flexible and scalable service for handling multiple cloud functions with different parameters
 */
export class CloudFunctionService {
  private functionRegistry: Record<string, CloudFunctionDefinition> = {};
  private callHistory: CallHistoryEntry[] = [];
  private pendingRequests: Map<string, Promise<CloudFunctionCallResult>> =
    new Map();
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private config: CloudFunctionServiceConfig;

  constructor(config: CloudFunctionServiceConfig = {}) {
    this.config = {
      defaultTimeout: 30000,
      defaultRetryAttempts: 3,
      defaultHeaders: {
        'Content-Type': 'application/json',
      },
      enableRequestDeduplication: true,
      enableDetailedLogging: true,
      maxCallHistorySize: 100,
      ...config,
    };
  }

  /**
   * Add a request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add a response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Remove a request interceptor by name
   */
  removeRequestInterceptor(name: string): void {
    this.requestInterceptors = this.requestInterceptors.filter(
      (interceptor) => interceptor.name !== name,
    );
  }

  /**
   * Remove a response interceptor by name
   */
  removeResponseInterceptor(name: string): void {
    this.responseInterceptors = this.responseInterceptors.filter(
      (interceptor) => interceptor.name !== name,
    );
  }

  /**
   * Register a cloud function definition
   */
  registerFunction(definition: CloudFunctionDefinition): void {
    this.functionRegistry[definition.name] = definition;
    if (this.config.enableDetailedLogging) {
      console.log(`✅ Registered cloud function: ${definition.name}`, {
        method: definition.defaultMethod || 'POST',
        useQueryParams: definition.useQueryParams || false,
        timeout: definition.defaultTimeout || this.config.defaultTimeout,
        requiredParams: definition.requiredParams,
      });
    }
  }

  /**
   * Get all registered functions
   */
  getRegisteredFunctions(): CloudFunctionDefinition[] {
    return Object.values(this.functionRegistry);
  }

  /**
   * Get function definition by name
   */
  getFunctionDefinition(
    functionName: string,
  ): CloudFunctionDefinition | undefined {
    return this.functionRegistry[functionName];
  }

  /**
   * Get function URL by name
   */
  getFunctionUrl(functionName: keyof typeof cloudFunctionUrls): string {
    const url = cloudFunctionUrls[functionName];
    if (!url) {
      throw new Error(`Unknown function: ${functionName}`);
    }
    return url;
  }

  /**
   * Generate a unique request ID for tracing
   */
  private generateRequestId(): string {
    return `cf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Create a cache key for request deduplication
   */
  private createCacheKey(
    functionName: string,
    params: Record<string, unknown>,
    config: CloudFunctionRequestConfig,
  ): string {
    const sortedParams = Object.keys(params)
      .sort()
      .map((key) => `${key}:${JSON.stringify(params[key])}`)
      .join('|');
    return `${functionName}:${config.method || 'POST'}:${sortedParams}`;
  }

  /**
   * Validate parameters against function definition
   */
  private validateParams(
    functionName: string,
    params: Record<string, unknown>,
  ): { valid: boolean; missingParams: string[]; error?: string } {
    const definition = this.functionRegistry[functionName];
    if (!definition) {
      return {
        valid: false,
        missingParams: [],
        error: `Cloud function '${functionName}' is not registered`,
      };
    }

    // Special validation for getJobDetailsByJobId function
    if (functionName === 'getJobDetailsByJobId') {
      const hasScribeJobId =
        params.scribeJobId &&
        params.scribeJobId !== null &&
        params.scribeJobId !== '' &&
        params.scribeJobId !== undefined;

      const hasAllThreeParams =
        params.appointmentId &&
        params.appointmentId !== null &&
        params.appointmentId !== '' &&
        params.appointmentId !== undefined &&
        params.locationId &&
        params.locationId !== null &&
        params.locationId !== '' &&
        params.locationId !== undefined &&
        params.patientId &&
        params.patientId !== null &&
        params.patientId !== '' &&
        params.patientId !== undefined;

      if (!hasScribeJobId && !hasAllThreeParams) {
        return {
          valid: false,
          missingParams: [],
          error:
            'Either scribeJobId must be provided, or all three parameters (appointmentId, locationId, patientId) must be provided',
        };
      }

      // Ensure both sets of parameters are not provided at the same time
      if (hasScribeJobId && hasAllThreeParams) {
        return {
          valid: false,
          missingParams: [],
          error:
            'Cannot provide both scribeJobId and the alternative parameters (appointmentId, locationId, patientId) at the same time',
        };
      }

      return { valid: true, missingParams: [] };
    }

    // Standard validation for other functions
    const missingParams = definition.requiredParams.filter(
      (param) =>
        !(param in params) ||
        params[param] === undefined ||
        params[param] === null ||
        params[param] === '',
    );

    return {
      valid: missingParams.length === 0,
      missingParams,
      error:
        missingParams.length > 0 ?
          `Missing required parameters: ${missingParams.join(', ')}`
        : undefined,
    };
  }

  /**
   * Build request URL with optional query parameters
   */
  private buildRequestUrl(
    baseUrl: string,
    params: Record<string, unknown>,
    useQueryParams: boolean,
  ): string {
    if (!useQueryParams) {
      return baseUrl;
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  /**
   * Build request body (for non-GET requests)
   */
  private buildRequestBody(
    params: Record<string, unknown>,
    useQueryParams: boolean,
    method: HttpMethod,
  ): string | undefined {
    if (method === 'GET' || useQueryParams) {
      return undefined;
    }
    return JSON.stringify(params);
  }

  /**
   * Apply request interceptors
   */
  private async applyRequestInterceptors(request: Request): Promise<Request> {
    let modifiedRequest = request;
    for (const interceptor of this.requestInterceptors) {
      try {
        modifiedRequest = await interceptor.intercept(modifiedRequest);
      } catch (error) {
        console.warn(`Request interceptor ${interceptor.name} failed:`, error);
      }
    }
    return modifiedRequest;
  }

  /**
   * Apply response interceptors
   */
  private async applyResponseInterceptors(
    response: Response,
    requestId: string,
  ): Promise<Response> {
    let modifiedResponse = response;
    for (const interceptor of this.responseInterceptors) {
      try {
        modifiedResponse = await interceptor.intercept(
          modifiedResponse,
          requestId,
        );
      } catch (error) {
        console.warn(`Response interceptor ${interceptor.name} failed:`, error);
      }
    }
    return modifiedResponse;
  }

  /**
   * Make HTTP request with enhanced error handling and logging
   */
  private async makeRequest(
    url: string,
    config: CloudFunctionRequestConfig,
    params: Record<string, unknown>,
    requestId: string,
  ): Promise<CloudFunctionCallResult> {
    const startTime = Date.now();
    const method = config.method || 'POST';
    const useQueryParams = config.useQueryParams || false;
    const timeout = config.timeout || this.config.defaultTimeout!;

    try {
      // Build URL and body
      const requestUrl = this.buildRequestUrl(url, params, useQueryParams);
      const body = this.buildRequestBody(params, useQueryParams, method);

      // Create request
      const requestInit: RequestInit = {
        method,
        headers: {
          ...this.config.defaultHeaders,
          ...config.headers,
        },
        signal: AbortSignal.timeout(timeout),
      };

      if (body) {
        requestInit.body = body;
      }

      const request = new Request(requestUrl, requestInit);

      // Apply request interceptors
      const interceptedRequest = await this.applyRequestInterceptors(request);

      // Log request if detailed logging is enabled
      if (this.config.enableDetailedLogging && !config.skipLogging) {
        console.log(`☁️ [${requestId}] Calling cloud function:`, {
          url: requestUrl,
          method,
          params: useQueryParams ? params : undefined,
          body: !useQueryParams ? params : undefined,
          timeout,
        });
      }

      // Make the request
      const response = await fetch(interceptedRequest);

      // Apply response interceptors
      const interceptedResponse = await this.applyResponseInterceptors(
        response,
        requestId,
      );

      const responseTime = Date.now() - startTime;

      // Handle response
      if (!interceptedResponse.ok) {
        const errorText = await interceptedResponse.text();
        const result: CloudFunctionCallResult = {
          success: false,
          error: `HTTP ${interceptedResponse.status}: ${interceptedResponse.statusText} - ${errorText}`,
          statusCode: interceptedResponse.status,
          responseTime,
          requestId,
        };

        this.recordCall({
          timestamp: Date.now(),
          functionName: url,
          success: false,
          responseTime,
          error: result.error,
          statusCode: interceptedResponse.status,
          requestId,
          method,
          params,
        });

        return result;
      }

      // Parse response
      let data: unknown;
      const contentType = interceptedResponse.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await interceptedResponse.json();
      } else {
        data = await interceptedResponse.text();
      }

      const result: CloudFunctionCallResult = {
        success: true,
        data,
        statusCode: interceptedResponse.status,
        responseTime,
        requestId,
      };

      this.recordCall({
        timestamp: Date.now(),
        functionName: url,
        success: true,
        responseTime,
        statusCode: interceptedResponse.status,
        requestId,
        method,
        params,
      });

      return result;
    } catch (error) {
      const responseTime = Date.now() - startTime;
      let errorMessage: string;

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = `Request timeout after ${timeout}ms`;
        } else {
          errorMessage = error.message;
        }
      } else {
        errorMessage = 'Unknown error occurred while calling cloud function';
      }

      const result: CloudFunctionCallResult = {
        success: false,
        error: errorMessage,
        responseTime,
        requestId,
      };

      this.recordCall({
        timestamp: Date.now(),
        functionName: url,
        success: false,
        responseTime,
        error: errorMessage,
        requestId,
        method,
        params,
      });

      return result;
    }
  }

  /**
   * Call a specific cloud function by name with parameters
   * Enhanced version with better error handling and configuration
   */
  async callFunction(
    functionName: keyof typeof cloudFunctionUrls,
    params: Record<string, unknown> = {},
    config: CloudFunctionRequestConfig = {},
  ): Promise<CloudFunctionCallResult> {
    const url = cloudFunctionUrls[functionName];
    if (!url) {
      return { success: false, error: `Unknown function: ${functionName}` };
    }

    const definition = this.functionRegistry[functionName];
    const requestConfig: CloudFunctionRequestConfig = {
      method: definition?.defaultMethod || 'POST',
      useQueryParams: definition?.useQueryParams || false,
      timeout: definition?.defaultTimeout || this.config.defaultTimeout,
      headers: definition?.headers,
      ...config,
    };

    // Validate parameters
    const validation = this.validateParams(functionName, params);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
        requestId: this.generateRequestId(),
      };
    }

    // Handle request deduplication
    if (this.config.enableRequestDeduplication && requestConfig.cacheKey) {
      const cacheKey = requestConfig.cacheKey;
      if (this.pendingRequests.has(cacheKey)) {
        return this.pendingRequests.get(cacheKey)!;
      }
    }

    const requestId = this.generateRequestId();
    const requestPromise = this.makeRequest(
      url,
      requestConfig,
      params,
      requestId,
    );

    // Store pending request for deduplication
    if (this.config.enableRequestDeduplication && requestConfig.cacheKey) {
      this.pendingRequests.set(requestConfig.cacheKey, requestPromise);
      requestPromise.finally(() => {
        this.pendingRequests.delete(requestConfig.cacheKey!);
      });
    }

    return requestPromise;
  }

  /**
   * Call cloud function with retry logic
   */
  async callFunctionWithRetry(
    functionName: keyof typeof cloudFunctionUrls,
    params: Record<string, unknown> = {},
    config: CloudFunctionRequestConfig = {},
  ): Promise<CloudFunctionCallResult> {
    const definition = this.functionRegistry[functionName];
    const retryConfig = definition?.retryConfig || {
      maxRetries: this.config.defaultRetryAttempts || 3,
      backoffMultiplier: 2,
      initialDelay: 1000,
      retryableStatusCodes: [408, 429, 500, 502, 503, 504],
    };

    let lastError: string | undefined;
    let lastResult: CloudFunctionCallResult | undefined;

    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      if (attempt > 0) {
        const delay =
          retryConfig.initialDelay *
          Math.pow(retryConfig.backoffMultiplier, attempt - 1);

        if (this.config.enableDetailedLogging) {
          console.log(
            `🔄 Retrying cloud function '${functionName}' (attempt ${
              attempt + 1
            }/${retryConfig.maxRetries + 1}) in ${delay}ms`,
          );
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      const result = await this.callFunction(functionName, params, config);
      lastResult = result;

      if (result.success) {
        return { ...result, retryCount: attempt };
      }

      lastError = result.error;

      // Don't retry on client errors (4xx) unless specifically configured
      if (
        result.statusCode &&
        result.statusCode >= 400 &&
        result.statusCode < 500 &&
        !retryConfig.retryableStatusCodes?.includes(result.statusCode)
      ) {
        if (this.config.enableDetailedLogging) {
          console.log(
            `❌ Not retrying due to client error (${result.statusCode})`,
          );
        }
        break;
      }
    }

    return {
      ...lastResult!,
      error: `Failed after ${
        retryConfig.maxRetries + 1
      } attempts. Last error: ${lastError}`,
      retryCount: retryConfig.maxRetries,
    };
  }

  /**
   * Call cloud function for integration endpoint
   */
  async callForIntegrationEndpoint(
    functionName: keyof typeof cloudFunctionUrls,
    secretId: string,
    username: string,
    endpoints: IntegrationEndpoint[],
    action: string,
    environment: string = envConfig.env,
    additionalParams: Record<string, unknown> = {},
    config: CloudFunctionRequestConfig = {},
  ): Promise<CloudFunctionCallResult> {
    const endpoint = endpoints.find(
      (ep) => ep.action === action && ep.environment === environment,
    );

    if (!endpoint) {
      return {
        success: false,
        error: `Endpoint not found for action: ${action} and environment: ${environment}`,
        requestId: this.generateRequestId(),
      };
    }

    return this.callFunction(
      functionName,
      {
        secretId,
        username,
        url: endpoint.endpoint_url,
        action,
        environment,
        ...additionalParams,
      },
      config,
    );
  }

  /**
   * Record call in history with enhanced information
   */
  private recordCall(entry: CallHistoryEntry): void {
    this.callHistory.push(entry);
    if (this.callHistory.length > this.config.maxCallHistorySize!) {
      this.callHistory = this.callHistory.slice(
        -this.config.maxCallHistorySize!,
      );
    }
  }

  /**
   * Get call history with enhanced information
   */
  getCallHistory(): CallHistoryEntry[] {
    return [...this.callHistory];
  }

  /**
   * Get call statistics with enhanced metrics
   */
  getCallStatistics(): {
    totalCalls: number;
    successfulCalls: number;
    failedCalls: number;
    averageResponseTime: number;
    functionStats: Record<
      string,
      {
        calls: number;
        success: number;
        failure: number;
        avgResponseTime: number;
        lastCallTime?: number;
        errorRate: number;
      }
    >;
    recentErrors: Array<{
      timestamp: number;
      functionName: string;
      error: string;
      requestId: string;
    }>;
  } {
    const totalCalls = this.callHistory.length;
    const successfulCalls = this.callHistory.filter(
      (call) => call.success,
    ).length;
    const failedCalls = totalCalls - successfulCalls;
    const averageResponseTime =
      totalCalls > 0 ?
        this.callHistory.reduce((sum, call) => sum + call.responseTime, 0) /
        totalCalls
      : 0;

    const functionStats: Record<
      string,
      {
        calls: number;
        success: number;
        failure: number;
        avgResponseTime: number;
        lastCallTime?: number;
        errorRate: number;
      }
    > = {};

    // Group calls by function name
    this.callHistory.forEach((call) => {
      if (!functionStats[call.functionName]) {
        functionStats[call.functionName] = {
          calls: 0,
          success: 0,
          failure: 0,
          avgResponseTime: 0,
          errorRate: 0,
        };
      }
      const stats = functionStats[call.functionName];
      stats.calls++;
      stats.lastCallTime = call.timestamp;
      if (call.success) {
        stats.success++;
      } else {
        stats.failure++;
      }
    });

    // Calculate averages and error rates
    Object.keys(functionStats).forEach((functionName) => {
      const functionCalls = this.callHistory.filter(
        (call) => call.functionName === functionName,
      );
      const stats = functionStats[functionName];
      stats.avgResponseTime =
        functionCalls.length > 0 ?
          functionCalls.reduce((sum, call) => sum + call.responseTime, 0) /
          functionCalls.length
        : 0;
      stats.errorRate =
        stats.calls > 0 ? (stats.failure / stats.calls) * 100 : 0;
    });

    // Get recent errors (last 10)
    const recentErrors = this.callHistory
      .filter((call) => !call.success && call.error)
      .slice(-10)
      .map((call) => ({
        timestamp: call.timestamp,
        functionName: call.functionName,
        error: call.error!,
        requestId: call.requestId,
      }));

    return {
      totalCalls,
      successfulCalls,
      failedCalls,
      averageResponseTime,
      functionStats,
      recentErrors,
    };
  }

  /**
   * Clear call history
   */
  clearCallHistory(): void {
    this.callHistory = [];
    if (this.config.enableDetailedLogging) {
      console.log('🗑️ Cloud Function call history cleared');
    }
  }

  /**
   * Get pending requests count
   */
  getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Clear all pending requests
   */
  clearPendingRequests(): void {
    this.pendingRequests.clear();
  }
}

// Create default service instance with enhanced configuration
export const cloudFunctionService = new CloudFunctionService({
  enableDetailedLogging: true,
  enableRequestDeduplication: true,
  maxCallHistorySize: 200,
});

// All cloud functions have been migrated to the gateway API (see gateway-service.ts).
// No function registrations needed.

export default cloudFunctionService;
